import React, { useRef, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

export interface ReportNameEditorProps {
  required: boolean;
  content: string;
  setContent: (content: string) => void;
  mergeTags?: { title: string; value: string }[];
  error?: boolean;
  helperText?: string;
  labelText?: string;
  onBlur?: () => void;
}

const PREFIX = '{|';
const SUFFIX = '|}';

export default function ReportNameEditor({
  required,
  content,
  setContent,
  mergeTags = [],
  error,
  helperText,
  labelText,
  onBlur,
}: ReportNameEditorProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const [cursorPos, setCursorPos] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [lastCursorPos, setLastCursorPos] = useState<number>(0);
  // Get all tags like {|...|} from the content
  const getExistingTags = () => {
    const matches: string[] = [];
    const regex = /\{\|(.*?)\|\}/g;
    let match;
    while ((match = regex.exec(content))) {
      matches.push(match[1]);
    }
    return matches;
  };

  // Input field change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const cursor = e.target.selectionStart ?? 0;
    setLastCursorPos(cursor);
    if (newValue.slice(0, cursor).endsWith(PREFIX)) {
      setCursorPos(cursor);
      setOpen(true);
    } else if (newValue.slice(0, cursor).endsWith(SUFFIX)) {
      setOpen(false);
    }

    setContent(newValue);
  };

  // Prevent editing inside tags or deleting partial tags
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = inputRef.current;
    if (!input) return;

    const cursor = input.selectionStart ?? 0;
    const tagRegex = /\{\|.*?\|\}/g;
    let match;

    while ((match = tagRegex.exec(content)) !== null) {
      const tagStart = match.index;
      const tagEnd = tagStart + match[0].length;

      if (cursor > tagStart && cursor < tagEnd) {
        e.preventDefault();
        setTimeout(() => input.setSelectionRange(tagEnd, tagEnd), 0);
        return;
      }

      if (cursor === tagEnd && e.key === 'Backspace') {
        e.preventDefault();
        setContent(content.slice(0, tagStart) + content.slice(tagEnd));
        setTimeout(() => input.setSelectionRange(tagStart, tagStart), 0);
        return;
      }
    }

    if (e.key === 'Enter') e.preventDefault();
  };

  // Prevent selection inside tag
  const handleSelect = () => {
    const input = inputRef.current;
    if (!input) return;

    const cursor = input.selectionStart ?? 0;
    const tagRegex = /\{\|.*?\|\}/g;
    let match;

    while ((match = tagRegex.exec(content)) !== null) {
      const tagStart = match.index;
      const tagEnd = tagStart + match[0].length;

      if (cursor > tagStart && cursor < tagEnd) {
        const goingRight = cursor > lastCursorPos;
        const newCursor = goingRight ? tagEnd : tagStart;
        setTimeout(() => input.setSelectionRange(newCursor, newCursor), 0);
        break;
      }
    }
    setLastCursorPos(cursor);
  };

  const handleSelectTag = (tag: string) => {
    const existingTags = getExistingTags();
    const input = inputRef.current;

    if (existingTags.includes(tag) || cursorPos === null || !input) {
      setOpen(false);
      return;
    }

    const before = content.slice(0, cursorPos - PREFIX.length);
    const after = content.slice(cursorPos);
    const baseTag = `${PREFIX}${tag}${SUFFIX}`;
    const isAtEnd = cursorPos === content.length;

    let inserted = baseTag;
    if (content.length >= 50 && isAtEnd) {
      const needsSpaceAfter = after.length === 0 || !/^\s/.test(after);
      inserted = `${baseTag}${needsSpaceAfter ? ' ' : ''}`;
    }

    const newValue = before + inserted + after;
    setContent(newValue);
    setOpen(false);

    setTimeout(() => {
      const newCursor = before.length + inserted.length;
      input.focus();
      input.setSelectionRange(newCursor, newCursor);
    }, 0);
  };

  // Sync scroll between mirror and input
  useEffect(() => {
    const input = inputRef.current;
    const mirror = mirrorRef.current;
    if (!input || !mirror) return;

    const syncScroll = () => {
      mirror.scrollTop = input.scrollTop;
    };

    input.addEventListener('scroll', syncScroll);
    return () => input.removeEventListener('scroll', syncScroll);
  }, []);

  const existingTags = getExistingTags();
  return (
    <Box sx={{ position: 'relative', width: 450 }}>
      <TextField
        required={required}
        fullWidth
        inputRef={inputRef}
        onBlur={onBlur}
        label={labelText ? labelText : 'Report Name'}
        value={content}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        error={error}
        helperText={helperText}
        multiline={false}
        sx={{
          position: 'relative',
          backgroundColor: 'transparent',
          '& .MuiInputBase-input': () => ({
            backgroundColor: 'transparent',
            whiteSpace: 'nowrap',
            position: 'relative',
            zIndex: 2,
          }),
        }}
      />

      <Popper
        open={open && mergeTags.length > 0}
        anchorEl={inputRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <Paper elevation={3} sx={{ width: 300 }}>
          <List dense>
            {mergeTags.map((tag) => {
              const disabled = existingTags.includes(tag.value);
              return (
                <ListItem key={tag.value} disablePadding>
                  <ListItemButton
                    disabled={disabled}
                    onClick={() => handleSelectTag(tag.value)}
                  >
                    <ListItemText
                      primary={tag.title}
                      primaryTypographyProps={{
                        color: disabled ? 'text.disabled' : 'text.primary',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
}
